/**
 * viagem.ts — como uma viagem se chama quando o espaço é pouco.
 *
 * O nome "de verdade" de uma viagem é o roteiro: `destinos.join(' → ')`. Isso
 * funciona no cadastro e na tela de detalhe, e **não** funciona na pílula do dia
 * na agenda — "Rio de Janeiro → Niterói" toma o cabeçalho inteiro e empurra o
 * número do dia e os botões (Vittorio, 09/08).
 *
 * Aqui mora a regra que gera o **nome curto sugerido**. Ela é usada em dois
 * momentos, e é o mesmo texto nos dois:
 *   1. no cadastro da viagem, preenchendo o campo "nome curto" que a Suany pode
 *      editar (migration 048);
 *   2. na exibição, como fallback de quem ainda não tem nome curto gravado —
 *      por isso nenhuma viagem antiga precisou de backfill.
 *
 * A máquina sugere, a pessoa decide: encurtar nome de lugar é editorial, e
 * dicionário nenhum cobre a próxima cidade que aparecer.
 */

/** Abreviações que a equipe já usa falando. Chave normalizada (sem acento,
 *  minúscula) porque o destino é digitado à mão e vem como vier.
 *  Crescer esta lista é o jeito barato de melhorar a sugestão — e nunca trava
 *  ninguém, porque o campo é editável. */
const ABREVIACOES: Record<string, string> = {
  'rio de janeiro': 'Rio',
  'nova friburgo': 'Friburgo',
  'armacao dos buzios': 'Búzios',
  'cabo frio': 'Cabo Frio',
  'sao paulo': 'São Paulo',
  'belo horizonte': 'BH',
  'nova iguacu': 'Nova Iguaçu',
  'duque de caxias': 'Caxias',
  'rio das ostras': 'Rio das Ostras',
  'sao pedro da aldeia': 'São Pedro',
  'arraial do cabo': 'Arraial',
  'porto seguro': 'Porto Seguro',
};

/** Tira acento e caixa para comparar com o dicionário. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    // \p{Diacritic} em vez da faixa U+0300–U+036F escrita à mão: a faixa vira
    // caracteres invisíveis no arquivo e não sobrevive a uma cópia descuidada.
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();
}

/**
 * Encurta UM destino. Sem entrada no dicionário, corta o sufixo de estado
 * ("Niterói - RJ", "Niterói/RJ") e devolve o resto — quem digita às vezes põe,
 * às vezes não, e a sigla não ajuda a distinguir uma parada da outra.
 */
export function abreviarDestino(destino: string): string {
  const limpo = destino.replace(/\s*[-/,]\s*[A-Za-z]{2}\s*$/, '').trim();
  return ABREVIACOES[normalizar(limpo)] ?? limpo;
}

/**
 * O nome curto sugerido para um roteiro.
 *
 * - 1 parada  → a parada abreviada.
 * - 2 paradas → "A → B", se couber no teto; senão vira "A +1".
 * - 3+        → "A +N", porque listar o roteiro inteiro numa pílula de célula de
 *               calendário não é legível em tamanho nenhum. O roteiro completo
 *               continua no `title` e na tela da viagem.
 *
 * `maximo` é de exibição, não de armazenamento (a coluna aceita 40).
 */
export function sugerirNomeCurto(destinos: string[], maximo = 22): string {
  const paradas = (destinos ?? []).map((d) => (d ?? '').trim()).filter(Boolean);
  if (paradas.length === 0) return '';

  const curtas = paradas.map(abreviarDestino);
  if (curtas.length === 1) return cortar(curtas[0], maximo);

  if (curtas.length === 2) {
    const juntas = `${curtas[0]} → ${curtas[1]}`;
    if (juntas.length <= maximo) return juntas;
    return cortar(`${curtas[0]} +1`, maximo);
  }
  return cortar(`${curtas[0]} +${curtas.length - 1}`, maximo);
}

/** Último recurso: nome de uma parada só que ainda estoura o teto. */
function cortar(texto: string, maximo: number): string {
  return texto.length <= maximo ? texto : `${texto.slice(0, maximo - 1).trimEnd()}…`;
}

/**
 * O que a pílula mostra: o nome curto gravado, se houver; senão a sugestão.
 * Uma função só, para que agenda, cabeçalho do dia e card do instalador não
 * cheguem a textos diferentes para a mesma viagem.
 */
export function nomeCurtoDaViagem(
  nomeCurto: string | null | undefined,
  destinos: string[],
): string {
  const gravado = (nomeCurto ?? '').trim();
  if (gravado) return gravado;
  return sugerirNomeCurto(destinos);
}

/** O roteiro por extenso — o que vai no `title` e nas telas com espaço. */
export function roteiroCompleto(destinos: string[]): string {
  return (destinos ?? []).filter(Boolean).join(' → ');
}
